import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiClientError, type ApiClientErrorCode, type Partner } from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { PartnerCardView, type PartnerCardState } from './PartnerCardView';

/**
 * Container Экрана 2.8.3 «Карточка партнёра» (Блок 8, ADR-0080, Фаза D3).
 *
 * Живёт на `/partners/:id`. Грузит карточку через `apiClient.partners.get(id)`
 * (P-012, без прямого fetch). 5 состояний (P-021): loading → success / empty
 * (404 «Партнёр не найден») / error. idle поддержан Presenter'ом, но при сразу-
 * запросе не достигается.
 *
 * P-001 verbatim: react-router-dom v7 уже декодирует path-param, поэтому
 * `params.id` приходит verbatim id; apiClient encodeURIComponent-ит обратно при URL
 * (как FurnitureCard — БЕЗ повторного decode).
 *
 * Wiring: «Отзывы» → `/partners/:id/reviews` (саб-экран, empty в D3; реальные
 * отзывы — Фаза E). «Назад» (empty/error) → история (P-036).
 */
export function PartnerCard() {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';

  const [state, setState] = useState<PartnerCardState>('loading');
  const [partner, setPartner] = useState<Partner | undefined>(undefined);
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!id) {
      setState('empty');
      return;
    }
    setState('loading');
    setError(undefined);
    let cancelled = false;
    apiClient.partners.get(id).then(
      (p) => {
        if (cancelled) return;
        setPartner(p);
        setState('success');
      },
      (err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiClientError && err.code === 'NOT_FOUND') {
          setState('empty');
          return;
        }
        // Не leak-аем String(err) в UI (security): non-ApiClientError → пустой message.
        const apiErr =
          err instanceof ApiClientError
            ? { code: err.code, message: err.message }
            : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
        setError(apiErr);
        setState('error');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <PartnerCardView
      state={state}
      partner={partner}
      error={error}
      onOpenReviews={() => navigate(`/partners/${encodeURIComponent(id)}/reviews`)}
      onBack={() => navigate(-1)}
    />
  );
}
